import {
  AloftDate,
  PairedCostRouteItemWithQuery,
  isDefined,
  schAlertSeverity,
  schAlertStatus,
  schAlertType,
  schPairedCostRouteItem,
  schQueryParams,
  syntaxFNMA,
} from '@aloft/toolkit-shared';

import { CompPropertyDataState } from '../../../../redux/types.ts';
import { isSaleDateOutOfBounds } from '../../../../utils/locationTypeSaleDateRequirementMonths.ts';
import { AlertDefinition } from '../../types.ts';
import { getAlertTriggers } from '../utils/alertTriggers.ts';
import { getCompTypeLabel, getScopedAlertType } from '../utils/compTypeScoping.ts';

const RECENT_SALE_MONTHS = 6;

const isRecentSettledSale = (saleMonth: string | undefined, effectiveDate: AloftDate): boolean => {
  if (!saleMonth) return false;
  const saleDate = AloftDate.fromIsoString(`${saleMonth}-01`);
  return !isSaleDateOutOfBounds(saleDate, effectiveDate, RECENT_SALE_MONTHS);
};

const isActiveOrPending = (status: string | undefined): boolean =>
  status === syntaxFNMA.schUadListingStatus.enum.Active ||
  status === syntaxFNMA.schUadListingStatus.enum.Contract;

export const insufficientRecentComp: AlertDefinition = ({
  compSequence,
  compType,
  propertyDataEntities,
  order,
  effectiveDate,
}) => {
  const comps = compSequence
    .map((id) => propertyDataEntities[id])
    .filter(isDefined)
    .map((compData) => (compData as CompPropertyDataState).compData);

  const recentSettledSales = comps.filter(
    (comp) =>
      comp.editableData.transaction.status === syntaxFNMA.schUadListingStatus.enum.SettledSale &&
      isRecentSettledSale(comp.editableData.transaction.saleDate, effectiveDate),
  );

  const activeOrPendingComps = comps.filter((comp) =>
    isActiveOrPending(comp.editableData.transaction.status),
  );

  if (recentSettledSales.length >= 1 || activeOrPendingComps.length >= 2) return [];

  let message: string;
  switch (compType) {
    case 'aiv':
    case 'arv':
      message = `Fewer than 1 ${getCompTypeLabel(compType)} Comp is sold within 6 months from the effective date`;
      break;
    default:
      message = 'Fewer than 1 Comp is sold within 6 months from the effective date';
      break;
  }

  const fixRoute =
    `${schPairedCostRouteItem.enum.features}?${schQueryParams.enum.addComps}=true` as PairedCostRouteItemWithQuery;

  return [
    {
      type: getScopedAlertType(schAlertType.enum.insufficientRecentComp, compType),
      status: schAlertStatus.enum.active,
      severity: schAlertSeverity.enum.error,
      orderType: ['appraisify'],
      message,
      ...(compType ? { compType } : {}),
      fixingInfo: {
        tips: 'If more recent alternatives are unavailable, include at least 2 active or pending Comps to compensate for limited inventory.',
        pairedCostRouteItem: fixRoute,
        fixingMethod: ['action'],
        commentAlwaysRequired: false,
      },
      triggers: getAlertTriggers({
        fixRoute,
        order,
      }),
    },
  ];
};
